import {
  useDroppable,
  closestCenter,
  DndContext,
  DragOverlay,
  getFirstCollision,
  KeyboardSensor,
  MeasuringStrategy,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  type SortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { byPosition, cardComparator, type BoardSnapshot, type CardSort, type CardSummary, type Column, type Label, type Lane, type Member } from '@kaba/shared';
import { useMoveCard, useMoveColumn } from '../../lib/board';
import { cx } from '../ui/ui';
import { CardTile } from './CardTile';

/* IDs: Karten "c:<id>", Spalten "col:<id>" */
const cid = (id: string) => `c:${id}`;
const colId = (id: string) => `col:${id}`;
const isCol = (id: UniqueIdentifier) => String(id).startsWith('col:');
const raw = (id: UniqueIdentifier) => String(id).replace(/^(c|col):/, '');
/* Mit Zeilen ist ein Ablageziel „<Zeile>|<Spalte>“ */
const SEP = '|';
const cell = (lane: string, col: string) => `${lane}${SEP}${col}`;
const colOf = (container: string) => (container.includes(SEP) ? container.split(SEP)[1]! : container);
const laneOf = (container: string) => (container.includes(SEP) ? container.split(SEP)[0]! : null);
let lastDragEnd = 0;

export type Layout = 'board' | 'list';

/** Schrittweises Einhängen langer Spalten: zuerst so viele Karten, dann je Schritt weitere */
export const RENDER_FIRST = 40;
export const RENDER_STEP = 40;

/**
 * Anzahl eingehängter Karten. Eine gerade gezogene Karte ist immer dabei, dazu einige Karten
 * darunter – so lässt sie sich per Tastatur oder Maus über die Grenze hinaus weiterschieben.
 */
function shownCount(ids: string[], limit: number, activeId: string | null) {
  const ai = activeId ? ids.indexOf(activeId) : -1;
  return Math.min(ids.length, Math.max(limit, ai >= 0 ? ai + 6 : 0));
}

/**
 * Platzhalter am Ende einer langen Spalte: lädt weitere Karten nach, sobald er in die Nähe des
 * sichtbaren Bereichs kommt (IntersectionObserver), und ist zugleich eine Schaltfläche für
 * Tastatur und Screenreader.
 */
function MoreCards({ hidden, onMore, layout }: { hidden: number; onMore: () => void; layout: Layout }) {
  const ref = useRef<HTMLButtonElement>(null);
  const more = useRef(onMore);
  more.current = onMore;
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const root = el.closest('[data-scroll-root]') as HTMLElement | null;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && more.current(), { root, rootMargin: '1200px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hidden]);
  return (
    <button
      ref={ref}
      type="button"
      onClick={onMore}
      className={cx(
        'flex min-h-11 w-full shrink-0 items-center justify-center rounded-lg text-sm text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/5',
        layout === 'list' && 'rounded-none',
      )}
    >
      Weitere {hidden} {hidden === 1 ? 'Karte' : 'Karten'} anzeigen
    </button>
  );
}

/** Bei automatischer Sortierung keine Umsortier-Vorschau innerhalb der Spalte */
const noReorder: SortingStrategy = () => null;

export interface ColumnRenderProps {
  column: Column;
  cards: CardSummary[];
  /** Gesamtzahl inkl. ausgefilterter Karten (für WIP-Limit) */
  totalCount: number;
  children: ReactNode;
  /** Ziehgriff-Props für den Spaltenkopf */
  handleProps: Record<string, unknown>;
  isDragging: boolean;
  isOver: boolean;
}

export interface CardRenderProps {
  card: CardSummary;
  column: Column;
  index: number;
  dragging: boolean;
}

export interface LaneCellProps {
  column: Column;
  lane: Lane;
  cards: CardSummary[];
  children: ReactNode;
  isOver: boolean;
}

/** Zeilen (Swimlanes) – nur gesetzt, wenn die Person sie eingeschaltet hat */
export interface LaneSetup {
  list: Lane[];
  keyOf: (c: CardSummary) => string;
  /** Karte wurde in eine andere Zeile gezogen */
  onChange: (card: CardSummary, from: string, to: string) => void;
  renderHeader: (p: { lane: Lane; count: number; collapsed: boolean; toggle: () => void }) => ReactNode;
  renderColumnHeader: (p: { column: Column; count: number; totalCount: number }) => ReactNode;
  renderCell: (p: LaneCellProps) => ReactNode;
  /** Klassen für den Scroll-Container im Zeilenmodus */
  className: string;
}

interface Props {
  snapshot: BoardSnapshot;
  lanes?: LaneSetup | null;
  layout: Layout;
  /** Sortierung in den Spalten; nur „manual“ erlaubt freies Umordnen */
  sort?: CardSort;
  visible: (c: CardSummary) => boolean;
  canEdit: boolean;
  renderColumn: (p: ColumnRenderProps) => ReactNode;
  renderCard: (p: CardRenderProps) => ReactNode;
  renderOverlayCard?: (card: CardSummary) => ReactNode;
  onOpenCard: (id: string) => void;
  scrollerRef?: React.RefObject<HTMLDivElement | null>;
  className?: string;
  trailing?: ReactNode;
}

export function BoardDnd({
  snapshot,
  layout,
  sort = 'manual',
  visible,
  canEdit,
  renderColumn,
  renderCard,
  onOpenCard,
  scrollerRef,
  className,
  trailing,
  lanes,
}: Props) {
  const moveCard = useMoveCard(snapshot.board.id);
  const moveColumn = useMoveColumn(snapshot.board.id);

  const columns = useMemo(() => [...snapshot.columns].sort(byPosition), [snapshot.columns]);
  const cardById = useMemo(() => new Map(snapshot.cards.map((c) => [c.id, c])), [snapshot.cards]);
  const labels = useMemo(() => new Map<string, Label>(snapshot.labels.map((l) => [l.id, l])), [snapshot.labels]);
  const members = useMemo(() => new Map<string, Member>(snapshot.members.map((m) => [m.id, m])), [snapshot.members]);

  const serverItems = useMemo(() => {
    const m: Record<string, string[]> = {};
    if (lanes) {
      for (const lane of lanes.list) for (const col of columns) m[cell(lane.key, col.id)] = [];
      for (const c of [...snapshot.cards].sort(cardComparator(sort))) {
        const k = cell(lanes.keyOf(c), c.columnId);
        if (m[k] && visible(c)) m[k]!.push(c.id);
      }
      return m;
    }
    for (const col of columns) m[col.id] = [];
    for (const c of [...snapshot.cards].sort(cardComparator(sort))) {
      if (m[c.columnId] && visible(c)) m[c.columnId]!.push(c.id);
    }
    return m;
  }, [columns, snapshot.cards, visible, sort, lanes]);
  const serverColOrder = useMemo(() => columns.map((c) => c.id), [columns]);

  const [items, setItems] = useState(serverItems);
  // Lange Spalten schrittweise einhängen (Leistung): je Spalte bzw. Zelle zuerst RENDER_FIRST Karten
  const [limits, setLimits] = useState<Record<string, number>>({});
  const limitOf = (k: string) => limits[k] ?? RENDER_FIRST;
  const grow = useCallback((k: string) => setLimits((l) => ({ ...l, [k]: (l[k] ?? RENDER_FIRST) + RENDER_STEP })), []);
  const ensureShown = (k: string, index: number) => setLimits((l) => (index < (l[k] ?? RENDER_FIRST) ? l : { ...l, [k]: index + 1 }));
  const [colOrder, setColOrder] = useState(serverColOrder);
  const [active, setActive] = useState<UniqueIdentifier | null>(null);
  const dragging = useRef(false);
  const origin = useRef<{ columnId: string; index: number } | null>(null);
  const lastOver = useRef<UniqueIdentifier | null>(null);
  // Nach einem Spaltenwechsel bis zum nächsten Frame keine weitere Kollision auswerten – sonst kann die Karte
  // bei verschobenem Layout zwischen zwei Spalten hin- und herspringen (React-Fehler 185, Muster aus dnd-kit)
  const recentlyMoved = useRef(false);

  // Serverstand übernehmen, solange nicht gezogen wird
  useEffect(() => {
    if (!dragging.current) setItems(serverItems);
  }, [serverItems]);
  // Eben selbst angelegte Karten bleiben sichtbar, auch hinter der Einhänge-Grenze
  useEffect(() => {
    const show = (id: string) => {
      for (const [k, ids] of Object.entries(itemsRef.current)) {
        const i = ids.indexOf(id);
        if (i >= 0) return ensureShown(k, i);
      }
    };
    const on = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      // Erst nach dem nächsten Zeichnen steht die Karte in der Liste
      requestAnimationFrame(() => requestAnimationFrame(() => show(id)));
    };
    window.addEventListener('kaba-card-created', on);
    return () => window.removeEventListener('kaba-card-created', on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!dragging.current) setColOrder(serverColOrder);
  }, [serverColOrder]);

  const totalCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of snapshot.cards) m.set(c.columnId, (m.get(c.columnId) ?? 0) + 1);
    return m;
  }, [snapshot.cards]);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const colOrderRef = useRef(colOrder);
  colOrderRef.current = colOrder;
  const laneKeysRef = useRef<string[]>([]);
  laneKeysRef.current = lanes?.list.map((l) => l.key) ?? [];

  /**
   * Pfeil links/rechts mit einer Karte: direkt in die Nachbarspalte, auf etwa gleicher Höhe.
   * (Der Standard von dnd-kit zählt die eigene Spalte als „links“ und springt bei hohen
   * Spalten auch mal eine Spalte zu weit.)
   */
  const keyboardCoordinates: KeyboardCoordinateGetter = useCallback((event, args) => {
    const { active, droppableRects, collisionRect } = args.context;
    const step = event.code === 'ArrowLeft' ? -1 : event.code === 'ArrowRight' ? 1 : 0;
    const vstep = event.code === 'ArrowUp' ? -1 : event.code === 'ArrowDown' ? 1 : 0;
    // Mit Zeilen: am Ende einer Zelle hoch/runter in dieselbe Spalte der Nachbarzeile
    if (vstep && active && !isCol(active.id) && laneKeysRef.current.length) {
      const own = Object.entries(itemsRef.current).find(([, ids]) => ids.includes(raw(active.id)));
      const lane = own ? laneOf(own[0]) : null;
      if (own && lane !== null) {
        const idx = own[1].indexOf(raw(active.id));
        const atEdge = vstep > 0 ? idx === own[1].length - 1 : idx === 0;
        const keys = laneKeysRef.current;
        const nextLane = keys[keys.indexOf(lane) + vstep];
        const rect = atEdge && nextLane !== undefined ? droppableRects.get(colId(cell(nextLane, colOf(own[0])))) : undefined;
        if (rect) {
          event.preventDefault();
          return { x: rect.left + 8, y: vstep > 0 ? rect.top + 4 : Math.max(rect.top, rect.top + rect.height - (collisionRect?.height ?? 0)) };
        }
        if (atEdge) {
          event.preventDefault();
          return undefined;
        }
      }
    }
    if (!step || !active || isCol(active.id)) return sortableKeyboardCoordinates(event, args);
    event.preventDefault();
    const order = colOrderRef.current;
    const own = Object.entries(itemsRef.current).find(([, ids]) => ids.includes(raw(active.id)))?.[0];
    const lane = own ? laneOf(own) : null;
    const nextCol = own ? order[order.indexOf(colOf(own)) + step] : undefined;
    const target = nextCol ? (lane !== null ? cell(lane, nextCol) : nextCol) : undefined;
    const rect = target ? droppableRects.get(colId(target)) : undefined;
    if (!rect) return undefined;
    const height = collisionRect?.height ?? 0;
    const y = Math.min(Math.max(args.currentCoordinates.y, rect.top), Math.max(rect.top, rect.top + rect.height - height));
    return { x: rect.left + 8, y };
  }, []);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 260, tolerance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: keyboardCoordinates,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] },
    }),
  );

  const findContainer = useCallback(
    (id: UniqueIdentifier): string | null => {
      if (isCol(id)) return raw(id);
      const r = raw(id);
      for (const [col, ids] of Object.entries(items)) if (ids.includes(r)) return col;
      return null;
    },
    [items],
  );

  const activeIsColumn = active !== null && isCol(active);

  const collision: CollisionDetection = useCallback(
    (args) => {
      if (activeIsColumn) {
        return closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((c) => isCol(c.id)) });
      }
      if (recentlyMoved.current && args.active) return [{ id: args.active.id }];
      const pointer = pointerWithin(args);
      const hits = pointer.length > 0 ? pointer : rectIntersection(args);
      let overId = getFirstCollision(hits, 'id');
      if (overId != null) {
        if (isCol(overId)) {
          const inCol = items[raw(overId)] ?? [];
          if (inCol.length > 0) {
            const closest = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (c) => !isCol(c.id) && inCol.includes(raw(c.id)),
              ),
            })[0]?.id;
            if (closest) overId = closest;
          }
        }
        lastOver.current = overId;
        return [{ id: overId }];
      }
      return lastOver.current ? [{ id: lastOver.current }] : [];
    },
    [activeIsColumn, items],
  );

  const setScrollerDragging = (on: boolean) => scrollerRef?.current?.classList.toggle('is-dragging', on);

  const onDragStart = ({ active }: DragStartEvent) => {
    dragging.current = true;
    setActive(active.id);
    setScrollerDragging(true);
    if (!isCol(active.id)) {
      const col = findContainer(active.id);
      origin.current = col ? { columnId: col, index: items[col]!.indexOf(raw(active.id)) } : null;
    }
    if ('vibrate' in navigator) navigator.vibrate?.(8);
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (!over || isCol(active.id)) return;
    const from = findContainer(active.id);
    const to = findContainer(over.id);
    if (!from || !to || from === to) return;
    recentlyMoved.current = true;
    requestAnimationFrame(() => requestAnimationFrame(() => (recentlyMoved.current = false)));
    setItems((prev) => {
      const fromList = prev[from]!.filter((x) => x !== raw(active.id));
      const toList = [...prev[to]!];
      let index = toList.length;
      if (sort !== 'manual') {
        // Sortierte Spalte: Karte erscheint dort, wo sie nach der Sortierung hingehört
        const cmp = cardComparator(sort);
        const moving = cardById.get(raw(active.id));
        index = moving ? toList.filter((id) => { const c = cardById.get(id); return c && cmp(c, moving) < 0; }).length : toList.length;
      } else if (!isCol(over.id)) {
        const overIndex = toList.indexOf(raw(over.id));
        const activeRect = active.rect.current.translated;
        const below = activeRect && activeRect.top > over.rect.top + over.rect.height / 2;
        index = overIndex >= 0 ? overIndex + (below ? 1 : 0) : toList.length;
      }
      toList.splice(index, 0, raw(active.id));
      return { ...prev, [from]: fromList, [to]: toList };
    });
  };

  const finish = () => {
    lastDragEnd = Date.now();
    dragging.current = false;
    setActive(null);
    setScrollerDragging(false);
    lastOver.current = null;
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (isCol(active.id)) {
      if (over && isCol(over.id) && over.id !== active.id) {
        const oldIndex = colOrder.indexOf(raw(active.id));
        const newIndex = colOrder.indexOf(raw(over.id));
        const next = arrayMove(colOrder, oldIndex, newIndex);
        setColOrder(next);
        moveColumn.mutate({ columnId: raw(active.id), index: next.indexOf(raw(active.id)) });
      }
      finish();
      return;
    }
    const cardId = raw(active.id);
    const container = findContainer(active.id);
    if (!container || !over) {
      setItems(serverItems);
      finish();
      return;
    }
    const columnId = colOf(container);
    const fromContainer = origin.current?.columnId ?? null;
    const card = cardById.get(cardId);
    // Andere Zeile: Eigenschaft der Karte ändern (Hauptverantwortung, Priorität, Label)
    if (lanes && card && fromContainer) {
      const from = laneOf(fromContainer);
      const to = laneOf(container);
      if (from !== null && to !== null && from !== to) lanes.onChange(card, from, to);
    }
    if (sort !== 'manual') {
      // Nur Spaltenwechsel zählt; gespeichert wird am Ende der manuellen Reihenfolge
      if (fromContainer && colOf(fromContainer) === columnId) {
        if (fromContainer === container) setItems(serverItems);
      } else {
        const lastManual = snapshot.cards
          .filter((c) => c.columnId === columnId && c.id !== cardId)
          .sort(byPosition)
          .at(-1);
        moveCard.mutate({ cardId, columnId, beforeId: lastManual?.id ?? null, afterId: null });
      }
      finish();
      return;
    }
    let list = items[container]!;
    if (!isCol(over.id) && findContainer(over.id) === container) {
      const from = list.indexOf(cardId);
      const to = list.indexOf(raw(over.id));
      if (from !== to && to >= 0) list = arrayMove(list, from, to);
    }
    const next = { ...items, [container]: list };
    setItems(next);
    const index = list.indexOf(cardId);
    ensureShown(container, index);
    const unchanged = origin.current?.columnId === container && origin.current.index === index;
    if (!unchanged) {
      moveCard.mutate({
        cardId,
        columnId,
        beforeId: list[index - 1] ?? null,
        afterId: list[index + 1] ?? null,
      });
    }
    finish();
  };

  const onDragCancel = () => {
    setItems(serverItems);
    setColOrder(serverColOrder);
    finish();
  };

  const colById = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);

  const announcements: Announcements = {
    onDragStart: ({ active }) => `${describe(active.id)} aufgenommen.`,
    onDragOver: ({ active, over }) =>
      over ? `${describe(active.id)} über ${describeTarget(over.id)}.` : `${describe(active.id)} über keinem Ziel.`,
    onDragEnd: ({ active, over }) =>
      over ? `${describe(active.id)} abgelegt: ${describeTarget(over.id)}.` : `${describe(active.id)} abgelegt.`,
    onDragCancel: ({ active }) => `Verschieben abgebrochen. ${describe(active.id)} ist wieder an der alten Stelle.`,
  };
  function describe(id: UniqueIdentifier) {
    if (isCol(id)) return `Spalte „${colById.get(raw(id))?.title ?? ''}“`;
    return `Karte „${cardById.get(raw(id))?.title ?? ''}“`;
  }
  const laneTitle = (container: string) => {
    const l = laneOf(container);
    const lane = l !== null ? lanes?.list.find((x) => x.key === l) : null;
    return lane ? `Zeile „${lane.title}“, ` : '';
  };
  function describeTarget(id: UniqueIdentifier) {
    if (isCol(id)) {
      const c = colById.get(colOf(raw(id)));
      return `${laneTitle(raw(id))}Spalte „${c?.title ?? ''}“, Position ${(items[raw(id)]?.length ?? 0) + 1}`;
    }
    const container = findContainer(id);
    if (!container) return '';
    const list = items[container] ?? [];
    return `${laneTitle(container)}Spalte „${colById.get(colOf(container))?.title ?? ''}“, Position ${list.indexOf(raw(id)) + 1} von ${list.length}`;
  }

  const activeCard = active && !isCol(active) ? cardById.get(raw(active)) : null;
  const activeCardId = active && !isCol(active) ? raw(active) : null;
  const activeColumn = active && isCol(active) ? colById.get(raw(active)) : null;

  return (
    <DndContext
      sensors={canEdit ? sensors : undefined}
      collisionDetection={collision}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable:
            'Leertaste drückt, um aufzunehmen. Mit den Pfeiltasten verschieben, Leertaste zum Ablegen, Escape zum Abbrechen. Enter öffnet die Karte.',
        },
      }}
      autoScroll={{ threshold: { x: 0.1, y: 0.12 }, acceleration: 6 }}
    >
      {lanes ? (
        <LaneGrid
          lanes={lanes}
          layout={layout}
          boardId={snapshot.board.id}
          columns={colOrder.map((id) => colById.get(id)).filter((c): c is Column => !!c)}
          items={items}
          cardById={cardById}
          totalCounts={totalCounts}
          scrollerRef={scrollerRef}
          canEdit={canEdit}
          sort={sort}
          onOpenCard={onOpenCard}
          renderCard={renderCard}
          limitOf={limitOf}
          grow={grow}
          activeCardId={activeCardId}
        />
      ) : (
      <div ref={scrollerRef} className={className} data-scroll-root>
        <SortableContext
          items={colOrder.map(colId)}
          strategy={layout === 'board' ? horizontalListSortingStrategy : verticalListSortingStrategy}
          disabled={layout === 'list' || !canEdit}
        >
          {colOrder.map((id) => {
            const column = colById.get(id);
            if (!column) return null;
            const ids = items[id] ?? [];
            const cards = ids.map((x) => cardById.get(x)).filter((c): c is CardSummary => !!c);
            const shown = shownCount(ids, limitOf(id), activeCardId);
            return (
              <SortableColumn
                key={id}
                column={column}
                cards={cards}
                totalCount={totalCounts.get(id) ?? 0}
                render={renderColumn}
                disabled={layout === 'list' || !canEdit}
                fitHeight={layout === 'board'}
              >
                <SortableContext items={ids.slice(0, shown).map(cid)} strategy={sort === 'manual' ? verticalListSortingStrategy : noReorder}>
                  {cards.slice(0, shown).map((card, index) => (
                    <SortableCard key={card.id} id={card.id} disabled={!canEdit} onOpen={onOpenCard}>
                      {(isDragging) => renderCard({ card, column, index, dragging: isDragging })}
                    </SortableCard>
                  ))}
                </SortableContext>
                {shown < cards.length && <MoreCards hidden={cards.length - shown} onMore={() => grow(id)} layout={layout} />}
              </SortableColumn>
            );
          })}
        </SortableContext>
        {trailing}
      </div>
      )}
      {createPortal(
        <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
          {activeCard ? (
            <div className={layout === 'board' ? 'w-[min(85vw,288px)]' : 'w-[min(92vw,640px)]'}>
              <CardTile card={activeCard} labels={labels} members={members} overlay showCover={layout === 'board'} />
            </div>
          ) : activeColumn ? (
            <div className="w-[min(85vw,288px)] rotate-1 rounded-2xl bg-[var(--column)] p-3 font-semibold shadow-2xl ring-2 ring-accent/50">
              {activeColumn.title}
              <div className="mt-1 text-xs font-normal text-[var(--ink-soft)]">{items[activeColumn.id]?.length ?? 0} Karten</div>
            </div>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

function SortableColumn({
  column,
  cards,
  totalCount,
  render,
  children,
  disabled,
  fitHeight,
}: {
  column: Column;
  cards: CardSummary[];
  totalCount: number;
  render: (p: ColumnRenderProps) => ReactNode;
  children: ReactNode;
  disabled: boolean;
  /** Board: Spalte höchstens so hoch wie der sichtbare Bereich, Karten scrollen in der Spalte */
  fitHeight?: boolean;
}) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging, isOver } = useSortable({
    id: colId(column.id),
    // In der Liste (oder ohne Rechte) bleibt die Gruppe Ablageziel, ist aber nicht ziehbar
    disabled: { draggable: disabled, droppable: false },
    data: { type: 'column' },
  });
  return (
    <div
      ref={setNodeRef}
      style={disabled ? undefined : { transform: CSS.Translate.toString(transform), transition }}
      className={cx('kaba-column', fitHeight && 'flex max-h-full shrink-0 flex-col', isDragging && 'opacity-40')}
    >
      {render({
        column,
        cards,
        totalCount,
        children,
        handleProps: disabled ? {} : { ...attributes, ...ownEvents(listeners) },
        isDragging,
        isOver,
      })}
    </div>
  );
}

function SortableCard({
  id,
  disabled,
  onOpen,
  children,
}: {
  id: string;
  disabled: boolean;
  onOpen: (id: string) => void;
  children: (dragging: boolean) => ReactNode;
}) {
  const { setNodeRef, attributes, listeners: raw, transform, transition, isDragging } = useSortable({
    id: cid(id),
    disabled,
    data: { type: 'card' },
  });
  const listeners = ownEvents(raw);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      aria-roledescription="verschiebbare Karte"
      data-card-id={id}
      onClick={(e) => {
        if (!fromInside(e)) return; // z. B. Klick in einem Menü der Karte (Portal)
        if (Date.now() - lastDragEnd < 300) return;
        onOpen(id);
      }}
      onKeyDown={(e) => {
        if (!fromInside(e)) return;
        if (e.key === 'Enter' && !isDragging) {
          e.preventDefault();
          onOpen(id);
          return;
        }
        listeners?.onKeyDown?.(e);
      }}
      onContextMenu={(e) => {
        // Long-Press auf Touch soll kein Browser-Menü öffnen
        if (matchMedia('(pointer: coarse)').matches) e.preventDefault();
      }}
      className="rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {children(isDragging)}
    </div>
  );
}

/**
 * React reicht Ereignisse aus Portalen (Menüs, Dialoge) an die Eltern im Komponentenbaum weiter. Ziehgriffe
 * und Klickflächen reagieren nur auf Ereignisse aus ihrem eigenen DOM – sonst begann z. B. ein Klick im
 * Kartenmenü ein Ziehen oder öffnete die Karte (0.52; Menüs stoppen Zeigerereignisse bewusst nicht).
 */
function fromInside(e: { currentTarget: EventTarget; target: EventTarget }): boolean {
  return (e.currentTarget as Node).contains(e.target as Node);
}
type Listeners = ReturnType<typeof useSortable>['listeners'];
function ownEvents(listeners: Listeners): Listeners {
  if (!listeners) return listeners;
  const out: Record<string, (e: { currentTarget: EventTarget; target: EventTarget }) => void> = {};
  for (const [k, fn] of Object.entries(listeners) as [string, (e: unknown) => void][]) out[k] = (e) => (fromInside(e) ? fn(e) : undefined);
  return out as Listeners;
}

/* ---------- Zeilen (Swimlanes) ---------- */

const COLLAPSE_KEY = 'kaba.lanes.collapsed';
function loadCollapsed(boardId: string): string[] {
  try {
    return (JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? '{}') as Record<string, string[]>)[boardId] ?? [];
  } catch {
    return [];
  }
}
function saveCollapsed(boardId: string, keys: string[]) {
  try {
    const all = JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? '{}') as Record<string, string[]>;
    all[boardId] = keys;
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(all));
  } catch {
    /* privater Modus */
  }
}

function LaneGrid({
  lanes,
  layout,
  boardId,
  columns,
  items,
  cardById,
  totalCounts,
  scrollerRef,
  canEdit,
  sort,
  onOpenCard,
  renderCard,
  limitOf,
  grow,
  activeCardId,
}: {
  lanes: LaneSetup;
  layout: Layout;
  boardId: string;
  columns: Column[];
  items: Record<string, string[]>;
  cardById: Map<string, CardSummary>;
  totalCounts: Map<string, number>;
  scrollerRef?: React.RefObject<HTMLDivElement | null>;
  canEdit: boolean;
  sort: CardSort;
  onOpenCard: (id: string) => void;
  renderCard: (p: CardRenderProps) => ReactNode;
  limitOf: (k: string) => number;
  grow: (k: string) => void;
  activeCardId: string | null;
}) {
  const [collapsed, setCollapsed] = useState<string[]>(() => loadCollapsed(boardId));
  const toggle = (key: string) =>
    setCollapsed((c) => {
      const next = c.includes(key) ? c.filter((k) => k !== key) : [...c, key];
      saveCollapsed(boardId, next);
      return next;
    });
  const colCount = (colId: string) => lanes.list.reduce((n, l) => n + (items[cell(l.key, colId)]?.length ?? 0), 0);

  return (
    <div ref={scrollerRef} className={lanes.className} data-scroll-root>
      {layout === 'board' && (
        <div className="sticky top-0 z-20 flex w-max gap-3 bg-[var(--surface)] pb-2 pt-1">
          {columns.map((col) => (
            <div key={col.id} className="w-[min(85vw,288px)] shrink-0 sm:w-[288px]">
              {lanes.renderColumnHeader({ column: col, count: colCount(col.id), totalCount: totalCounts.get(col.id) ?? 0 })}
            </div>
          ))}
        </div>
      )}
      {lanes.list.map((lane) => {
        const isCollapsed = collapsed.includes(lane.key);
        const count = columns.reduce((n, col) => n + (items[cell(lane.key, col.id)]?.length ?? 0), 0);
        return (
          <section key={lane.key} className={cx(layout === 'board' ? 'mb-3 w-max' : 'mb-4')} aria-label={`Zeile ${lane.title}`}>
            <div className={cx(layout === 'board' && 'sticky left-0 z-10 w-[min(100vw-2rem,100%)]')}>
              {lanes.renderHeader({ lane, count, collapsed: isCollapsed, toggle: () => toggle(lane.key) })}
            </div>
            {!isCollapsed && (
              <div className={cx(layout === 'board' ? 'flex items-stretch gap-3' : 'flex flex-col gap-2')}>
                {columns.map((column) => {
                  const key = cell(lane.key, column.id);
                  const ids = items[key] ?? [];
                  const cards = ids.map((x) => cardById.get(x)).filter((c): c is CardSummary => !!c);
                  const shown = shownCount(ids, limitOf(key), activeCardId);
                  return (
                    <LaneCellDrop key={key} id={key} layout={layout}>
                      {(isOver) =>
                        lanes.renderCell({
                          column,
                          lane,
                          cards,
                          isOver,
                          children: (
                            <>
                              <SortableContext items={ids.slice(0, shown).map(cid)} strategy={sort === 'manual' ? verticalListSortingStrategy : noReorder}>
                                {cards.slice(0, shown).map((card, index) => (
                                  <SortableCard key={card.id} id={card.id} disabled={!canEdit} onOpen={onOpenCard}>
                                    {(isDragging) => renderCard({ card, column, index, dragging: isDragging })}
                                  </SortableCard>
                                ))}
                              </SortableContext>
                              {shown < cards.length && <MoreCards hidden={cards.length - shown} onMore={() => grow(key)} layout={layout} />}
                            </>
                          ),
                        })
                      }
                    </LaneCellDrop>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function LaneCellDrop({ id, layout, children }: { id: string; layout: Layout; children: (isOver: boolean) => ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: colId(id), data: { type: 'cell' } });
  return (
    <div ref={setNodeRef} className={cx(layout === 'board' && 'w-[min(85vw,288px)] shrink-0 sm:w-[288px]')}>
      {children(isOver)}
    </div>
  );
}
